/**
 * Shared field-control markup, option lists, and validation for the Register Agent / Edit Agent
 * forms (F031, form-spec `FS-F031-AGENT`:
 * `.specify/features/_active/FEA-F031-agent-registry/forms/AgentRegistration.form-spec.md`).
 *
 * Two distinct form components (`AgentRegisterForm`, `AgentEditForm` — ADR-013, one form type per
 * API command) share this option-list + tag-input logic rather than duplicating it.
 *
 * SUPERSEDED (Charter M3 migration): the `V-` validator functions below and
 * `mapServerErrorToFormState` are no longer wired into `AgentRegisterForm.tsx` / `AgentEditForm.tsx`
 * — those now validate via the imported Zod schemas (`AgentRegister.schema.ts` /
 * `AgentEdit.schema.ts`, `react-hook-form` + `zodResolver`) and map server errors via the shared
 * `src/components/forms/applyServerErrors.ts`. This module is kept, unchanged, because the enum
 * option lists (`AGENT_ENVIRONMENT_OPTIONS`, `DATA_ACCESS_LEVEL_OPTIONS`, `TOOL_ACCESS_LEVEL_OPTIONS`)
 * and `CapabilitiesTagInput` are still the schemas'/forms' live source of truth. See
 * `AgentRegister.schema.ts` / `AgentEdit.schema.ts` for the live validators (same rule IDs, same
 * verbatim messages).
 */
import { useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import {
  Button,
  Input,
  TagGroup,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { AgentEnvironment, DataAccessLevel, ToolAccessLevel } from '../../../types';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order — Field Dictionary F-06/F-10/F-11)
// ---------------------------------------------------------------------------

export const AGENT_ENVIRONMENT_OPTIONS: AgentEnvironment[] = ['Development', 'Test', 'Staging', 'Production'];
export const DATA_ACCESS_LEVEL_OPTIONS: DataAccessLevel[] = ['None', 'Public', 'Internal', 'Confidential', 'Regulated'];
export const TOOL_ACCESS_LEVEL_OPTIONS: ToolAccessLevel[] = ['None', 'ReadOnly', 'Invoke', 'Write', 'ExecuteCode'];

// ---------------------------------------------------------------------------
// Validation Matrix (`V-`) — one function = one rule, verbatim error messages.
// ---------------------------------------------------------------------------

/** V-01 — F-01 Agent Name, required @ submit. */
export function agentNameRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Agent name is required';
}
/** V-02 — F-01 Agent Name, max 200 chars @ blur. */
export function agentNameMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Agent name must be 200 characters or fewer' : undefined;
}

/** V-03 — F-02 Agent Version, required @ submit. */
export function agentVersionRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Agent version is required';
}
/** V-04 — F-02 Agent Version, max 50 chars @ blur. */
export function agentVersionMaxLengthError(value: string): string | undefined {
  return value.length > 50 ? 'Agent version must be 50 characters or fewer' : undefined;
}

/** V-05 — F-03 Purpose, required @ submit. */
export function purposeRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Purpose is required';
}
/** V-06 — F-03 Purpose, max 1000 chars @ blur. */
export function purposeMaxLengthError(value: string): string | undefined {
  return value.length > 1000 ? 'Purpose must be 1000 characters or fewer' : undefined;
}

/** V-07 — F-04 Owner Id, required (create only) @ submit. */
export function ownerIdRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Owner id is required';
}
/** V-08 — F-04 Owner Id, max 200 chars @ blur. */
export function ownerIdMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Owner id must be 200 characters or fewer' : undefined;
}

/** V-09 — F-05 Owner Team, max 200 chars @ blur (optional field). */
export function ownerTeamMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Owner team must be 200 characters or fewer' : undefined;
}

/** V-10 — F-06 Environment, one of AgentEnvironment @ submit. */
export function environmentRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select an environment';
}

/** V-11 — F-07 Model Provider, required @ submit. */
export function modelProviderRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Model provider is required';
}
/** V-12 — F-07 Model Provider, max 100 chars @ blur. */
export function modelProviderMaxLengthError(value: string): string | undefined {
  return value.length > 100 ? 'Model provider must be 100 characters or fewer' : undefined;
}

/** V-13 — F-08 Model Name, required @ submit. */
export function modelNameRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Model name is required';
}
/** V-14 — F-08 Model Name, max 200 chars @ blur. */
export function modelNameMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Model name must be 200 characters or fewer' : undefined;
}

/** V-15 — F-09 Runtime Provider, max 100 chars @ blur (optional field). */
export function runtimeProviderMaxLengthError(value: string): string | undefined {
  return value.length > 100 ? 'Runtime provider must be 100 characters or fewer' : undefined;
}

/** V-16 — F-10 Data Access Level, one of DataAccessLevel @ submit. */
export function dataAccessLevelRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a data access level';
}

/** V-17 — F-11 Tool Access Level, one of ToolAccessLevel @ submit. */
export function toolAccessLevelRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a tool access level';
}

/** V-18 — F-14 Metadata (JSON), valid JSON when non-empty @ blur. INFERRED provenance. */
export function metadataJsonError(value: string): string | undefined {
  if (!value.trim()) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch {
    return 'Metadata must be valid JSON';
  }
}

// ---------------------------------------------------------------------------
// B-03 — Server-error mapping. Maps `Result` failure payloads (camelCase OR PascalCase field
// keys — the API has no camelCase JSON contract resolver today, see api.ts `fetchWithAuth`) onto
// the matching field's error state; anything unmapped becomes the form-level MessageBar text.
// ---------------------------------------------------------------------------

export type FieldErrorMap = Record<string, string>;

interface ServerErrorShape {
  message?: string;
  details?: Record<string, string[] | string>;
}

export function mapServerErrorToFormState(
  error: unknown,
  knownFields: readonly string[]
): { fieldErrors: FieldErrorMap; globalMessage?: string } {
  const fieldErrors: FieldErrorMap = {};
  let globalMessage: string | undefined;

  const shaped = error as ServerErrorShape;
  const details = shaped?.details;
  if (details && typeof details === 'object') {
    for (const [key, val] of Object.entries(details)) {
      // Newtonsoft's default PascalCase output (`AgentName`) -> camelCase field key (`agentName`).
      const camelKey = key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
      const message = Array.isArray(val) ? val.join(' ') : String(val);
      if (knownFields.includes(camelKey)) {
        fieldErrors[camelKey] = message;
      } else {
        globalMessage = globalMessage ? `${globalMessage} ${message}` : message;
      }
    }
  }

  if (Object.keys(fieldErrors).length === 0 && !globalMessage) {
    globalMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  }

  return { fieldErrors, globalMessage };
}

// ---------------------------------------------------------------------------
// F-13 — Capabilities tag input (add/remove chips -> string[]). Shared by the Register form
// (Capabilities is not part of the Edit form's A-02 editable subset).
// ---------------------------------------------------------------------------

const useTagInputStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  inputRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  input: {
    flexGrow: 1,
  },
});

export interface CapabilitiesTagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export const CapabilitiesTagInput: FC<CapabilitiesTagInputProps> = ({ value, onChange, disabled }) => {
  const styles = useTagInputStyles();
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const trimmed = draft.trim();
    if (!trimmed || value.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const removeTag = (tag: string) => onChange(value.filter((t) => t !== tag));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inputRow}>
        <Input
          className={styles.input}
          value={draft}
          disabled={disabled}
          placeholder="Add capability tags"
          onChange={(_, data) => setDraft(data.value)}
          onKeyDown={handleKeyDown}
        />
        <Button appearance="secondary" disabled={disabled || !draft.trim()} onClick={addTag}>
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <TagGroup aria-label="Capabilities" onDismiss={(_, data) => removeTag(String(data.value))}>
          {value.map((tag) => (
            <InteractionTag key={tag} value={tag} disabled={disabled}>
              <InteractionTagPrimary>{tag}</InteractionTagPrimary>
              <InteractionTagSecondary aria-label={`Remove ${tag}`} />
            </InteractionTag>
          ))}
        </TagGroup>
      )}
    </div>
  );
};
